import React from "react";
import styled from "styled-components";

interface DropdownSelectorProps {
  value: string;
  options: Array<{ key: string; label: string }>;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  id?: string;
  minWidth?: string;
  showBorder?: boolean;
  variant?: "default" | "compact" | "compactSmall"; // Variant: default, compact, or compactSmall (smaller arrow)
}

const DropdownSelector: React.FC<DropdownSelectorProps> = ({
  value,
  options,
  onChange,
  disabled = false,
  id,
  minWidth = "80px",
  showBorder = true,
  variant = "default",
}) => {
  const selectId = id ? `${id}-select` : undefined;
  const arrowId = id ? `${id}-arrow` : undefined;
  return (
    <DropdownSelectorWrapper
      id={id}
      style={{ minWidth }}
      $showBorder={showBorder}
      $variant={variant}
    >
      <StyledSelect
        id={selectId}
        value={value}
        onChange={onChange}
        disabled={disabled}
        $variant={variant}
      >
        {options.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </StyledSelect>
      <DropdownArrow id={arrowId} $variant={variant}>
        <svg
          width="12"
          height="16"
          viewBox="0 0 12 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Up arrow - pixelated triangle pointing up */}
          <rect x="5.5" y="0" width="1" height="1" fill="currentColor" />
          <rect x="5" y="1" width="2" height="1" fill="currentColor" />
          <rect x="4" y="2" width="4" height="1" fill="currentColor" />
          <rect x="3" y="3" width="6" height="1" fill="currentColor" />
          <rect x="2" y="4" width="8" height="1" fill="currentColor" />
          <rect x="1" y="5" width="10" height="1" fill="currentColor" />
          {/* Gap between arrows */}
          {/* Down arrow - pixelated triangle pointing down */}
          <rect x="1" y="10" width="10" height="1" fill="currentColor" />
          <rect x="2" y="11" width="8" height="1" fill="currentColor" />
          <rect x="3" y="12" width="6" height="1" fill="currentColor" />
          <rect x="4" y="13" width="4" height="1" fill="currentColor" />
          <rect x="5" y="14" width="2" height="1" fill="currentColor" />
          <rect x="5.5" y="15" width="1" height="1" fill="currentColor" />
        </svg>
      </DropdownArrow>
    </DropdownSelectorWrapper>
  );
};

export default DropdownSelector;

// Styled Components
const DropdownSelectorWrapper = styled.div<{
  $showBorder: boolean;
  $variant: "default" | "compact" | "compactSmall";
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? "0"
      : "4px 6px"};
  border: ${(props) => {
    if (!props.$showBorder) return "none";
    return props.$variant === "compact" || props.$variant === "compactSmall"
      ? "2px solid #ccc"
      : "2px solid #CFC9C9";
  }};
  border-radius: 4px;
`;

const StyledSelect = styled.select<{
  $variant: "default" | "compact" | "compactSmall";
}>`
  flex: 1;
  padding: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? "2px 16px 2px 4px"
      : "8px 20px 8px 8px"};
  background-color: transparent;
  border: none;
  font-family: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      : '"Dogica Pixel", monospace'};
  font-size: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? "10px"
      : "10px"};
  line-height: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? "1.2"
      : "normal"};
  color: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? "#000"
      : "#cfc9c9"};
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  text-align: center;
  ${(props) =>
    props.$variant === "default"
      ? `
    image-rendering: pixelated;
    image-rendering: -moz-crisp-edges;
    image-rendering: crisp-edges;
  `
      : ""}
  outline: none;

  &:hover:not(:disabled) {
    color: ${(props) =>
      props.$variant === "compact" || props.$variant === "compactSmall"
        ? "#333"
        : "#d0d0d0"};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    color: #555;
    cursor: not-allowed;
  }

  option {
    background-color: #2a2a2a;
    color: ${(props) =>
      props.$variant === "compact" || props.$variant === "compactSmall"
        ? "#000"
        : "#cfc9c9"};
    font-family: ${(props) =>
      props.$variant === "compact" || props.$variant === "compactSmall"
        ? '"Manrope", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : '"Dogica Pixel", monospace'};
  }
`;

const DropdownArrow = styled.div<{
  $variant: "default" | "compact" | "compactSmall";
}>`
  position: absolute;
  right: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? "4px"
      : "8px"};
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${(props) =>
    props.$variant === "compact" || props.$variant === "compactSmall"
      ? "#000"
      : "#cfc9c9"};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
    width: ${(props) =>
      props.$variant === "compactSmall"
        ? "10px"
        : props.$variant === "compact"
        ? "12px"
        : "12px"};
    height: ${(props) =>
      props.$variant === "compactSmall"
        ? "10px"
        : props.$variant === "compact"
        ? "16px"
        : "16px"};
  }
`;
